import { ExternalLink, Phone, MapPin, Clock, Mail, DollarSign, Building, CreditCard } from "lucide-react";

const HelpPage = () => {
  const refundLinks = [
    {
      name: "Federal (IRS)",
      description: "Check your federal tax refund status with the IRS",
      url: "https://sa.www4.irs.gov/wmr/refund_status",
      icon: "🇺🇸",
    },
    {
      name: "IRS Amended Return",
      description: "Check status of your amended tax return",
      url: "https://www.irs.gov/filing/wheres-my-amended-return",
      icon: "📄",
    },
    {
      name: "Washington DC",
      description: "Check your DC tax refund status",
      url: "https://otr.cfo.dc.gov/es/service/check-your-tax-refund-status",
      icon: "🏛️",
    },
    {
      name: "Maryland",
      description: "Check your Maryland state tax refund status",
      url: "https://interactive.marylandtaxes.gov/INDIV/refundstatus/home.aspx",
      icon: "🦀",
    },
    {
      name: "Virginia",
      description: "Check your Virginia state tax refund status",
      url: "https://www.tax.virginia.gov/wheres-my-refund",
      icon: "🏛️",
    },
  ];

  const paymentLinks = [
    {
      name: "Federal IRS Direct Pay",
      description: "Pay federal taxes directly to the IRS",
      url: "https://www.irs.gov/payments/direct-pay",
      icon: "🇺🇸",
    },
    {
      name: "Washington DC Payment",
      description: "Pay DC taxes online via MyTax.DC.gov",
      url: "https://www.mytax.dc.gov",
      icon: "🏛️",
    },
    {
      name: "Maryland Payment",
      description: "Pay Maryland state taxes online",
      url: "https://interactive.marylandtaxes.gov/Individuals/Payment/",
      icon: "🦀",
    },
    {
      name: "Virginia Payment",
      description: "Pay Virginia state taxes online",
      url: "https://www.tax.virginia.gov/payments",
      icon: "🏛️",
    },
    {
      name: "IRS Payment Plans",
      description: "Set up IRS payment plan or view account",
      url: "https://www.irs.gov/payments/online-account-for-individuals",
      icon: "📋",
    },
    {
      name: "Apply for EIN",
      description: "Get an Employer Identification Number (EIN)",
      url: "https://sa.www4.irs.gov/applyein/",
      icon: "📝",
    },
    {
      name: "Alliance Association Bank Payment",
      description: "Make or confirm your association payment online",
      url: "https://pay.allianceassociationbank.com/Payment/ConfirmationPayment",
      icon: "🏦",
    },
    {
      name: "Prince George's County Tax Inquiry",
      description: "Look up Prince George's County MD tax records",
      url: "https://taxinquiry.princegeorgescountymd.gov/",
      icon: "🔎",
    },
    {
      name: "Gordon James Realty — Account History",
      description: "View your billing and account history",
      url: "https://home.gordonjamesrealty.com/billing/account-history",
      icon: "🏘️",
    },
    {
      name: "DC MyTax — Client Account",
      description: "DC Office of Tax and Revenue online client services",
      url: "https://mytax.dc.gov/_/#19",
      icon: "🏛️",
    },
  ];

  return (
    <div className="pt-20 min-h-screen" data-testid="help-page">
      {/* Header */}
      <section className="py-16 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Resources</span>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-4 font-serif">
            Tax Help & Resources
          </h1>
          <p className="text-muted-foreground mt-4">
            Official contact information and helpful links for federal and state tax offices.
          </p>
        </div>
      </section>

      {/* Where's My Refund Section */}
      <section className="py-12 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="w-7 h-7 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-semibold font-serif">Where's My Refund?</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {refundLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/50 p-4 transition-all duration-300"
                data-testid={`refund-link-${link.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{link.icon}</span>
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-[#D4AF37] transition-colors">{link.name}</h3>
                    <p className="text-sm text-muted-foreground">{link.description}</p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-[#D4AF37]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Online Payment Links */}
      <section className="py-12 border-b border-white/5 bg-[#0A0A0A]/50">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="w-7 h-7 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-semibold font-serif">Online Tax Payments</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paymentLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between bg-[#111] border border-white/5 hover:border-[#D4AF37]/50 p-4 transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{link.icon}</span>
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-[#D4AF37] transition-colors text-sm">{link.name}</h3>
                    <p className="text-xs text-muted-foreground">{link.description}</p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-[#D4AF37]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Office Contact Information */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-8">
            <Building className="w-7 h-7 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-semibold font-serif">Tax Office Contact Information</h2>
          </div>

          <div className="space-y-8">

            {/* IRS Federal */}
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <h3 className="text-xl font-semibold font-serif text-[#D4AF37] mb-4 flex items-center gap-2">
                <span>🇺🇸</span> Internal Revenue Service (IRS)
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Individual Taxpayer Help</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:8008291040" className="hover:text-[#D4AF37]">800-829-1040</a></p>
                    <p className="text-xs text-muted-foreground ml-5">Mon-Fri: 7AM - 7PM (local time)</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Business Tax Help</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:8008294933" className="hover:text-[#D4AF37]">800-829-4933</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Payment Plan / Collections</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:8008297650" className="hover:text-[#D4AF37]">800-829-7650</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Refund Information (Automated)</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:8008291954" className="hover:text-[#D4AF37]">800-829-1954</a></p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Mailing Address</p>
                  <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-1" /> 
                    <span>Department of the Treasury<br/>Internal Revenue Service<br/>Kansas City, MO 64999-0002</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Washington DC */}
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <h3 className="text-xl font-semibold font-serif text-[#D4AF37] mb-4 flex items-center gap-2">
                <span>🏛️</span> Washington DC Office of Tax and Revenue (OTR)
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Customer Service</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:2027274829" className="hover:text-[#D4AF37]">(202) 727-4TAX (4829)</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Toll-Free (Outside DC)</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:18009871117" className="hover:text-[#D4AF37]">1-800-987-1117</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Hours</p>
                    <p className="flex items-center gap-2"><Clock size={14} className="text-[#D4AF37]" /> Mon-Fri: 8:15 AM - 5:30 PM</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Walk-In Service Center</p>
                    <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-1" /> 
                      <span>1101 4th Street SW, Suite W270<br/>Washington, DC 20024</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Mailing Address</p>
                    <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-1" /> 
                      <span>Office of Tax and Revenue<br/>PO Box 96169/96147<br/>Washington, DC 20090-6169</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Maryland */}
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <h3 className="text-xl font-semibold font-serif text-[#D4AF37] mb-4 flex items-center gap-2">
                <span>🦀</span> Maryland — Comptroller of Maryland
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Individual Income Tax</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:4102607980" className="hover:text-[#D4AF37]">410-260-7980</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Toll-Free (Inside Maryland)</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:18006382937" className="hover:text-[#D4AF37]">1-800-MD-TAXES (1-800-638-2937)</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Payment Plan / Collections</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:4102607966" className="hover:text-[#D4AF37]">410-260-7966</a></p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email (General Tax Help)</p>
                    <p className="flex items-center gap-2"><Mail size={14} className="text-[#D4AF37]" /> <a href="mailto:taxhelp@marylandtaxes.gov" className="hover:text-[#D4AF37]">taxhelp@marylandtaxes.gov</a></p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Mailing Address</p>
                  <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-1" /> 
                    <span>Comptroller of Maryland<br/>Revenue Administration Division<br/>110 Carroll Street<br/>Annapolis, MD 21411-0001</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Virginia */}
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <h3 className="text-xl font-semibold font-serif text-[#D4AF37] mb-4 flex items-center gap-2">
                <span>🏛️</span> Virginia Department of Taxation
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Customer Service</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:8043678031" className="hover:text-[#D4AF37]">804-367-8031</a></p>
                    <p className="text-xs text-muted-foreground ml-5">Mon-Fri: 8:30 AM - 5:00 PM</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Collections / Payment Plan</p>
                    <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> <a href="tel:8043678045" className="hover:text-[#D4AF37]">804-367-8045</a></p>
                    <p className="text-xs text-muted-foreground ml-5">Payment plans, balance due, collections</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Mailing Address</p>
                    <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-1" /> 
                      <span>Virginia Department of Taxation<br/>P.O. Box 1115<br/>Richmond, VA 23218-1115</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Physical Office</p>
                    <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-1" /> 
                      <span>1957 Westmoreland Street<br/>Richmond, VA 23230</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Need Help CTA */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-[#111] border border-[#D4AF37]/30 p-8 text-center">
            <h3 className="text-2xl font-semibold font-serif mb-4">Need Help With Your Taxes?</h3>
            <p className="text-muted-foreground mb-6">
              Let Gold Spot Tax handle your tax preparation. Walk-ins welcome!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="tel:+12027042459"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3 hover:bg-[#C5A059] transition-colors"
              >
                <Phone size={18} />
                Call Us: (202) 704-2459
              </a>
              <a 
                href="mailto:tax@goldspottax.com"
                className="inline-flex items-center justify-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-6 py-3 hover:bg-[#D4AF37]/10 transition-colors"
              >
                <Mail size={18} />
                tax@goldspottax.com
              </a>
            </div>
            <p className="text-muted-foreground text-sm mt-6">
              📍 738 Longfellow St NW Unit 107, Washington, DC • Open 7 Days, 9AM-6PM
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpPage;
